import { adminApi } from "@/lib/axios";

// Types based on backend DTOs
export enum RewardStatus {
  PENDING = "PENDING",
  INCOMPLETE_ADDRESS = "INCOMPLETE_ADDRESS",
  DELIVERED = "DELIVERED",
}

export enum RewardType {
  AFFILIATE = "affiliate",
  BRAND = "brand",
  AGENCY = "agency",
}

export interface AffiliateReward {
  id: string;
  affiliateId: string;
  affiliateName: string;
  affiliateEmail: string;
  reward: string;
  rewardName: string;
  earningAmount: number;
  rewardStatus: RewardStatus;
  isActive: boolean;
  earnedAt: Date;
  deliveredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface BrandReward {
  id: string;
  brandDetailId: string;
  brandName: string;
  brandEmail: string;
  reward: string;
  rewardName: string;
  revenueAmount: number;
  rewardStatus: RewardStatus;
  isActive: boolean;
  earnedAt: Date;
  deliveredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AgencyReward {
  id: string;
  agencyId: string;
  agencyName: string;
  agencyEmail: string;
  reward: string;
  rewardName: string;
  revenueAmount: number;
  rewardStatus: RewardStatus;
  isActive: boolean;
  earnedAt: Date;
  deliveredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface RewardSearchParams {
  page?: number;
  limit?: number;
  type: RewardType;
  affiliateId?: string;
  brandDetailId?: string;
  agencyId?: string;
  status?: RewardStatus;
}

export interface RewardSearchResponse {
  data: (AffiliateReward | BrandReward | AgencyReward)[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UpdateRewardStatusDto {
  rewardStatus: RewardStatus;
  notes?: string;
}

export interface RewardStats {
  totalRewards: number;
  pendingRewards: number;
  incompleteAddressRewards: number;
  deliveredRewards: number;
  totalRewardValue: number;
  type: string;
}

export class AdminRewardsService {
  // Get rewards with pagination and filtering (unified endpoint)
  async getRewards(params: RewardSearchParams): Promise<RewardSearchResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Add required type parameter
      queryParams.append("type", params.type);

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      // Add filter parameters based on type
      if (params.type === RewardType.AFFILIATE && params.affiliateId) {
        queryParams.append("affiliateId", params.affiliateId);
      }
      if (params.type === RewardType.BRAND && params.brandDetailId) {
        queryParams.append("brandDetailId", params.brandDetailId);
      }
      if (params.type === RewardType.AGENCY && params.agencyId) {
        queryParams.append("agencyId", params.agencyId);
      }
      if (params.status) queryParams.append("status", params.status);

      const response = await adminApi.get(`/rewards?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch rewards:", error);
      throw new Error("Failed to fetch rewards");
    }
  }

  // Update affiliate reward status
  async updateAffiliateRewardStatus(
    rewardId: string,
    updateDto: UpdateRewardStatusDto
  ): Promise<AffiliateReward> {
    try {
      const response = await adminApi.put(
        `/affiliate-rewards/rewards/${rewardId}/status`,
        updateDto
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update affiliate reward status:", error);
      throw new Error("Failed to update affiliate reward status");
    }
  }

  // Update brand reward status
  async updateBrandRewardStatus(
    rewardId: string,
    updateDto: UpdateRewardStatusDto
  ): Promise<BrandReward> {
    try {
      const response = await adminApi.put(
        `/brand-rewards/rewards/${rewardId}/status`,
        updateDto
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update brand reward status:", error);
      throw new Error("Failed to update brand reward status");
    }
  }

  // Update agency reward status
  async updateAgencyRewardStatus(
    rewardId: string,
    updateDto: UpdateRewardStatusDto
  ): Promise<AgencyReward> {
    try {
      const response = await adminApi.put(
        `/agency-rewards/rewards/${rewardId}/status`,
        updateDto
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update agency reward status:", error);
      throw new Error("Failed to update agency reward status");
    }
  }

  // Get reward statistics (unified endpoint)
  async getRewardStats(type: RewardType): Promise<RewardStats> {
    try {
      const response = await adminApi.get(`/rewards/stats?type=${type}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch reward stats:", error);
      return {
        totalRewards: 0,
        pendingRewards: 0,
        incompleteAddressRewards: 0,
        deliveredRewards: 0,
        totalRewardValue: 0,
        type,
      };
    }
  }

  // Process affiliate rewards
  async processAffiliateRewards(): Promise<{ message: string }> {
    try {
      const response = await adminApi.post(
        "/affiliate-rewards/check-affiliate-rewards"
      );
      return response.data;
    } catch (error) {
      console.error("Failed to process affiliate rewards:", error);
      throw new Error("Failed to process affiliate rewards");
    }
  }

  // Process brand rewards
  async processBrandRewards(): Promise<{ message: string }> {
    try {
      const response = await adminApi.post(
        "/brand-rewards/check-brand-rewards"
      );
      return response.data;
    } catch (error) {
      console.error("Failed to process brand rewards:", error);
      throw new Error("Failed to process brand rewards");
    }
  }

  // Process agency rewards
  async processAgencyRewards(): Promise<{ message: string }> {
    try {
      const response = await adminApi.post("/agency-rewards/process");
      return response.data;
    } catch (error) {
      console.error("Failed to process agency rewards:", error);
      throw new Error("Failed to process agency rewards");
    }
  }
}

export const adminRewardsService = new AdminRewardsService();
